/**
 * i18next Setup and Configuration
 *
 * This module initializes and configures i18next for the application
 */

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

import { 
  defaultLanguage, 
  browserLanguage, 
  SUPPORTED_LANGUAGES,
  isSupportedLanguage 
} from './i18n'

// Import language resources
import { enCommon, zhCommon } from '../locales'

const resources = {
  en: {
    common: enCommon,
  },
  zh: {
    common: zhCommon,
  },
}

// Language detection configuration
const detectionOptions = {
  // Order of language detection methods
  order: ['localStorage', 'navigator', 'htmlTag'],
  
  // Keys to look for in localStorage
  lookupLocalStorage: 'secureroom-language',
  
  // Cache user language
  caches: ['localStorage'],
  
  // Only detect supported languages
  checkWhitelist: true,
}

// Initialize i18next
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    
    // Default language
    fallbackLng: defaultLanguage,
    
    // Supported languages
    supportedLngs: SUPPORTED_LANGUAGES,
    
    // Language detection
    detection: detectionOptions,
    
    // Namespace configuration
    defaultNS: 'common',
    ns: ['common'],
    
    // Interpolation configuration
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    
    // Development configuration
    debug: import.meta.env.DEV,
    
    // React configuration
    react: {
      useSuspense: false, // Disable suspense for now
    },
    
    // Custom language validation
    load: 'languageOnly', // Only load language, not region variants
  })

// Custom language change handler
i18n.on('languageChanged', (lng) => {
  if (import.meta.env.DEV) {
    console.info(`i18n: Language changed to '${lng}'`)
  }
  
  // Validate the language
  if (!isSupportedLanguage(lng)) {
    console.warn(`i18n: Unsupported language '${lng}', falling back to '${defaultLanguage}'`)
    i18n.changeLanguage(defaultLanguage)
  }
})

// Set initial language based on detection or browser preference
const initialLanguage = i18n.language || browserLanguage
if (isSupportedLanguage(initialLanguage)) {
  i18n.changeLanguage(initialLanguage)
} else {
  i18n.changeLanguage(defaultLanguage)
}

export default i18n
